
import React, { useState, useMemo } from 'react';
import { Registrant, EventOption } from '../types';
import { Users, Search, Calendar, ChevronDown } from 'lucide-react';

interface PublicRosterProps {
  events: EventOption[];
  registrations: Registrant[];
}

const PublicRoster: React.FC<PublicRosterProps> = ({ events, registrations }) => {
  const [selectedEventId, setSelectedEventId] = useState<string>(events[0]?.id || '');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredRegistrants = useMemo(() => {
    if (!selectedEventId) return [];

    // The registrations passed here should already be the public/approved subset from App.tsx
    // But we double check isApproved for safety
    return registrations
      .filter(reg => reg.selectedEvents.includes(selectedEventId))
      .filter(reg => reg.isApproved === true) 
      .filter(reg => 
        reg.name.includes(searchTerm) ||
        reg.church.includes(searchTerm) ||
        (reg.roles?.join(' ') || reg.role).includes(searchTerm)
      )
      .sort((a, b) => {
        const roleA = a.assignments?.[selectedEventId]?.leadershipRole || '無';
        const roleB = b.assignments?.[selectedEventId]?.leadershipRole || '無';

        // Priority map for leadership roles
        const getPriority = (role: string) => {
          if (role === '隊長') return 1;
          if (role === '副隊長') return 2;
          if (role === '護理長') return 3;
          if (role === '副護理長') return 4;
          if (role !== '無' && role) return 5; // Other assigned roles
          return 99; // No role assigned
        };

        const pA = getPriority(roleA);
        const pB = getPriority(roleB);

        // Sort by role priority first (lower number means higher priority)
        if (pA !== pB) return pA - pB;

        // Secondary sort by church name
        return a.church.localeCompare(b.church);
      });
  }, [registrations, selectedEventId, searchTerm]);

  const selectedEvent = events.find(e => e.id === selectedEventId);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="text-center mb-8 animate-fade-in-up">
        <h2 className="text-3xl font-extrabold text-gray-900 mb-2 flex items-center justify-center">
          <Users className="mr-3 text-blue-700" size={32}/>
          梯隊分工名單
        </h2>
      </div>

      {/* Event Selector - Dropdown Style */}
      <div className="mb-8 max-w-xl mx-auto animate-fade-in-up delay-100 relative z-20">
        <div className="relative group">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <Calendar className="text-blue-700 group-focus-within:text-blue-800 transition-colors" size={22} />
          </div>
          <select
            value={selectedEventId}
            onChange={(e) => setSelectedEventId(e.target.value)}
            className="block w-full pl-12 pr-10 py-4 text-base md:text-lg font-bold text-gray-800 bg-white border-2 border-gray-200 rounded-2xl appearance-none focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 shadow-sm hover:border-blue-300 transition-all cursor-pointer truncate"
          >
            {events.map((evt) => (
              <option key={evt.id} value={evt.id} className="py-2">
                {evt.dateRange} — {evt.location}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
            <ChevronDown className="text-gray-400" size={20} />
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden min-h-[400px] animate-fade-in-up delay-200 relative z-10">
        {/* Toolbar */}
        <div className="p-6 border-b border-gray-100 bg-gray-50 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-3 w-full md:w-auto">
             <div className="bg-blue-800 text-white text-sm font-bold px-3 py-1 rounded-lg shadow-sm whitespace-nowrap">
               {selectedEvent?.dateRange}
             </div>
             <div className="text-lg font-bold text-gray-800 truncate">
               {selectedEvent?.location} 
               <span className="text-gray-500 text-sm ml-2 font-normal hidden sm:inline">
                  (本梯次顯示 {filteredRegistrants.length} 人)
               </span>
             </div>
          </div>
          
          <div className="relative w-full md:w-64 group">
             <Search className="absolute left-3 top-3 text-gray-400 group-focus-within:text-blue-500 transition-colors" size={18} />
             <input 
               type="text" 
               placeholder="搜尋姓名或教會..." 
               className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none text-sm transition-all"
               value={searchTerm}
               onChange={(e) => setSearchTerm(e.target.value)}
             />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-white text-gray-500 text-sm uppercase tracking-wider border-b border-gray-200">
                <th className="px-6 py-4 font-bold whitespace-nowrap">教會</th>
                <th className="px-6 py-4 font-bold whitespace-nowrap">姓名</th>
                <th className="px-6 py-4 font-bold whitespace-nowrap">醫宣角色</th>
                <th className="px-6 py-4 font-bold text-blue-600 bg-blue-50/30 whitespace-nowrap">任務分配</th>
                <th className="px-6 py-4 font-bold text-blue-600 bg-blue-50/30 whitespace-nowrap">職務</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredRegistrants.length > 0 ? (
                filteredRegistrants.map((reg, idx) => {
                   const assignment = reg.assignments?.[selectedEventId] || {};
                   return (
                    <tr 
                      key={reg.id} 
                      style={{ animationDelay: `${idx * 30}ms` }}
                      className="hover:bg-blue-50/30 transition-colors duration-150 animate-fade-in"
                    >
                      <td className="px-6 py-4 text-gray-600 font-medium whitespace-nowrap">{reg.church}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center font-bold text-gray-900">
                           {/* Icon is purely decorative here */}
                           {reg.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-600 text-sm">
                        <div className="flex flex-wrap gap-1">
                          {(reg.roles && reg.roles.length > 0 ? reg.roles : [reg.role]).map((role, idx) => (
                            <span key={idx} className="inline-block bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">
                              {role.split('~')[0].split('(')[0]}
                              {role === '學生' && reg.studentSpecialization && (
                                <span className="ml-1 text-blue-600">({reg.studentSpecialization})</span>
                              )}
                              {role === '西醫' && reg.westernMedicineSpecialty && (
                                <span className="ml-1 text-blue-600">({reg.westernMedicineSpecialty})</span>
                              )}
                              {role === '治療師' && reg.therapistSpecialty && (
                                <span className="ml-1 text-blue-600">({reg.therapistSpecialty})</span>
                              )}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {assignment.taskRole ? (
                          <span className="inline-flex items-center bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-bold shadow-sm">
                            {assignment.taskRole}
                            {assignment.taskSubDetail && (
                               <span className="ml-1 text-xs bg-white/50 px-1.5 rounded text-blue-900">
                                 {assignment.taskSubDetail}
                               </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs italic">尚未分配</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                         {assignment.leadershipRole && (
                            <span className="inline-block bg-purple-100 text-purple-800 px-2 py-1 rounded text-sm font-bold shadow-sm">
                              {assignment.leadershipRole}
                            </span>
                         )}
                      </td>
                    </tr>
                   );
                })
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-400 italic">
                    {registrations.length > 0 ? "沒有符合搜尋條件的資料" : "目前尚無已通過審核的人員"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PublicRoster;
